import React, { useCallback, useEffect, useState } from 'react';
import '../super-admin.css';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Typography, message, DatePicker, Card } from 'antd';
import { UserOutlined, CreditCardOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;

// Function to notify other pages about subscription changes
function notifySubscriptionChange() {
  // Dispatch a custom event that other pages can listen to
  window.dispatchEvent(new CustomEvent('subscriptionUpdated', { 
    detail: { timestamp: Date.now() } 
  }));
  
  // Also use localStorage as a fallback for cross-tab communication
  localStorage.setItem('subscriptionUpdateTrigger', Date.now().toString());
}

const roleOptions = [
  { label: 'Super Admin', value: 'super admin' },
  { label: 'Business Owner', value: 'business_owner' },
  { label: 'Viewer', value: 'viewer' },
];

const AllUsers = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [businessByUser, setBusinessByUser] = useState({}); // userId -> business account (first match)
  const [subscriptions, setSubscriptions] = useState([]); // list of available plans
  const [userPlanByUser, setUserPlanByUser] = useState({}); // userId -> { plan, start_date, end_date }
  const [modalOpen, setModalOpen] = useState(false);
  const [businessModalOpen, setBusinessModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user row when editing
  const [editingBusinessUser, setEditingBusinessUser] = useState(null); // user for business details editing
  const [form] = Form.useForm();
  const [businessForm] = Form.useForm();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const fetchBusinessForUsers = useCallback(async (list) => {
    try {
      const entries = await Promise.all(list.map(async (u) => {
        try {
          const res = await fetch(`${apiBase}/business-accounts?user_id=${encodeURIComponent(u.id)}&limit=1`, {
            headers: { 'Content-Type': 'application/json', ...authHeaders() }
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'Failed to load business');
          const first = Array.isArray(body?.items) && body.items.length ? body.items[0] : null;
          return [u.id, first];
        } catch {
          return [u.id, null];
        }
      }));
      const map = Object.fromEntries(entries);
      setBusinessByUser(map);
    } catch {}
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      // Use new subscription_plans source to align with user_subscriptions.plan_id
      const res = await fetch(`${apiBase}/subscription-plans?only_active=1`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to load subscription plans');
      const items = Array.isArray(body?.items) ? body.items : [];
      // Normalize to { id, name, price }
      const normalized = items.map(p => ({
        id: p.id,
        name: p.plan_name,
        price: p.monthly_price
      }));
      setSubscriptions(normalized);
      return normalized;
    } catch (e) {
      message.error(e.message || 'Error loading subscription plans');
      setSubscriptions([]);
      return [];
    }
  }, []);

  const fetchPlansForUsers = useCallback(async (list, forceRefreshPlans = false) => {
    // Always fetch fresh subscriptions when forceRefreshPlans is true, or if subscriptions is empty
    const plans = (forceRefreshPlans || !subscriptions.length) ? await fetchSubscriptions() : subscriptions;
    try {
      const entries = await Promise.all(list.map(async (u) => {
        try {
          // Add timestamp to prevent caching
          const timestamp = Date.now();
          const res = await fetch(`${apiBase}/user-subscriptions?user_id=${encodeURIComponent(u.id)}&limit=1&sort=started_at&order=desc&_t=${timestamp}`, {
            headers: { 'Content-Type': 'application/json', ...authHeaders() }
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'Failed to load user subscription');
          const item = Array.isArray(body?.items) && body.items.length ? body.items[0] : null;
          if (!item) return [u.id, null];
          const planId = item?.plan_id != null ? item.plan_id : item?.subscription_id;
          const plan = plans.find(p => String(p.id) === String(planId)) || null;
          const start = item.started_at || item.start_date;
          const end = item.expires_at || item.end_date;
          return [u.id, { id: item.id, plan, start_date: start, end_date: end }];
        } catch {
          return [u.id, null];
        }
      }));
      setUserPlanByUser(Object.fromEntries(entries));
    } catch {}
  }, [subscriptions, fetchSubscriptions]);

  const fetchUsers = useCallback(async (forceRefreshPlans = false) => {
    setLoading(true);
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const res = await fetch(`${apiBase}/admin/users?_t=${timestamp}`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const body = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(body.error || 'Failed to load users');
      const list = Array.isArray(body) ? body : [];
      setUsers(list);
      // fetch related business details
      await fetchBusinessForUsers(list);
      // fetch user subscription plans with force refresh option
      await fetchPlansForUsers(list, forceRefreshPlans);
    } catch (err) {
      message.error('Error loading users');
    } finally {
      setLoading(false);
    }
  }, [fetchBusinessForUsers, fetchPlansForUsers]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    // Determine if current user is Super Admin from localStorage 'user'
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        const role = String(u?.role || '').toLowerCase();
        setIsSuperAdmin(role.includes('super'));
      }
    } catch {
      setIsSuperAdmin(false);
    }
  }, []);

  useEffect(() => { fetchSubscriptions(); }, [fetchSubscriptions]);


  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = useCallback((record) => {
    setEditing(record);
    const currentPlan = userPlanByUser[record.id]?.plan?.id;
    form.setFieldsValue({ name: record.name, email: record.email, role: record.role || undefined, planId: currentPlan || undefined, password: undefined });
    setModalOpen(true);
  }, [form, userPlanByUser]);

  const handleDelete = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Delete failed');
      message.success('User deleted');
      await fetchUsers(true);
    } catch (e) {
      message.error(e.message || 'Error deleting user');
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);


  const openBusinessEdit = useCallback((user) => {
    setEditingBusinessUser(user);
    const business = businessByUser[user.id];
    businessForm.setFieldsValue({
      company_name: business?.company_name || '',
      industry: business?.industry || '',
      subscription_plan: business?.subscription_plan || '',
      website: business?.website || '',
      address: business?.address || '',
      contact: business?.contact || '',
      business_email: business?.business_email || '',
      plan_start_date: undefined, // Always start fresh for plan dates
      plan_end_date: undefined
    });
    setBusinessModalOpen(true);
  }, [businessByUser, businessForm]);

  const handleBusinessSubmit = async () => {
    try {
      const values = await businessForm.validateFields();
      setLoading(true);
      
      const business = businessByUser[editingBusinessUser.id];
      const currentPlan = business?.subscription_plan;
      const newPlan = values.subscription_plan;
      
      // Prepare business account data (excluding plan dates)
      const businessData = {
        company_name: values.company_name,
        industry: values.industry,
        subscription_plan: values.subscription_plan,
        website: values.website,
        business_email: values.business_email,
        contact: values.contact,
        address: values.address
      };
      
      // Handle business account creation/update
      if (business?.id) {
        // Update existing business account
        const res = await fetch(`${apiBase}/business-accounts/${business.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(businessData),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Update failed');
      } else {
        // Create new business account
        const payload = {
          user_id: editingBusinessUser.id,
          ...businessData
        };
        const res = await fetch(`${apiBase}/business-accounts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Create failed');
      }
      
      // Handle subscription plan changes if plan is different
      if (newPlan && newPlan !== currentPlan) {
        // Find current user subscription for this user
        const currentUserPlan = userPlanByUser[editingBusinessUser.id];
        
        // End current subscription immediately if exists
        if (currentUserPlan?.id) {
          const resEnd = await fetch(`${apiBase}/user-subscriptions/${currentUserPlan.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ end_date: new Date().toISOString() })
          });
          const bEnd = await resEnd.json().catch(() => ({}));
          if (!resEnd.ok) throw new Error(bEnd.error || 'Failed to end previous subscription');
        }
        
        // Find the subscription plan ID by name
        const planToAssign = subscriptions.find(s => s.name.toLowerCase() === newPlan.toLowerCase());
        if (planToAssign) {
          // Create new subscription starting today
          const startDate = values.plan_start_date ? values.plan_start_date.toISOString() : new Date().toISOString();
          const endDate = values.plan_end_date ? values.plan_end_date.toISOString() : null;
          
          const subscriptionPayload = { 
            user_id: editingBusinessUser.id, 
            plan_id: planToAssign.id,
            start_date: startDate,
            end_date: endDate
          };
          
          const resSub = await fetch(`${apiBase}/user-subscriptions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(subscriptionPayload)
          });
          const b2 = await resSub.json().catch(() => ({}));
          if (!resSub.ok) throw new Error(b2.error || 'Failed to assign new subscription');
          
          message.success('Business details and subscription plan updated successfully');
          // Notify other pages about subscription change
          notifySubscriptionChange();
        } else {
          message.success('Business details updated successfully');
        }
      } else {
        message.success('Business details updated successfully');
      }
      
      // Refresh all user data including subscriptions with force refresh
      await fetchUsers(true);
      setBusinessModalOpen(false);
      businessForm.resetFields();
    } catch (err) {
      if (err?.errorFields) return; // antd form validation error
      message.error(err.message || 'Error saving business details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editing) {
        const payload = { name: values.name, email: values.email, role: values.role };
        if (values.password) payload.password = values.password;
        const res = await fetch(`${apiBase}/admin/users/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Update failed');
        message.success('User updated');
        // If a plan is selected and different from current, end previous and start new
        const current = userPlanByUser[editing.id];
        const currentPlanId = current?.plan?.id;
        if (values.planId && String(values.planId) !== String(currentPlanId || '')) {
          // End previous active subscription if exists
          if (current?.id) {
            const resEnd = await fetch(`${apiBase}/user-subscriptions/${current.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ end_date: new Date().toISOString() })
            });
            const bEnd = await resEnd.json().catch(() => ({}));
            if (!resEnd.ok) throw new Error(bEnd.error || 'Failed to end previous subscription');
          }
          // Start new subscription
          const resSub = await fetch(`${apiBase}/user-subscriptions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ user_id: editing.id, plan_id: values.planId })
          });
          const b2 = await resSub.json().catch(() => ({}));
          if (!resSub.ok) throw new Error(b2.error || 'Failed to set subscription');

          // Also update the Business Details card's plan label by syncing business account
          const biz = businessByUser[editing.id];
          const selectedPlan = subscriptions.find(s => String(s.id) === String(values.planId));
          if (biz?.id && selectedPlan?.name) {
            // Optimistic UI update
            setBusinessByUser(prev => ({
              ...prev,
              [editing.id]: {
                ...(prev[editing.id] || {}),
                subscription_plan: selectedPlan.name.toLowerCase()
              }
            }));
            // Persist to backend
            try {
              await fetch(`${apiBase}/business-accounts/${biz.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ subscription_plan: selectedPlan.name.toLowerCase() })
              });
            } catch {}
          }
        }
      } else {
        const res = await fetch(`${apiBase}/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(values),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Create failed');
        message.success('User created');
        // If a plan is selected, create a user-subscription entry for the new user
        if (values.planId && body?.id) {
          const resSub = await fetch(`${apiBase}/user-subscriptions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ user_id: body.id, plan_id: values.planId })
          });
          const b2 = await resSub.json().catch(() => ({}));
          if (!resSub.ok) throw new Error(b2.error || 'Failed to set subscription');
        }
      }
      await fetchUsers(true);
      setModalOpen(false);
      form.resetFields();
    } catch (err) {
      if (err?.errorFields) return; // antd form validation error
      message.error(err.message || 'Error saving user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 16 }}>All Users</Title>
      <div className="section" style={{ background: '#fff', borderRadius: 12, padding: 16 }}>
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" onClick={openCreate}>Add User</Button>
          <Button onClick={fetchUsers} disabled={loading}>Refresh</Button>
        </Space>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16
        }}>
          {users.map(u => {
            const biz = businessByUser[u.id] || null;
            const up = userPlanByUser[u.id] || null;
            const isUserSuper = String(u.role || '').toLowerCase().includes('super');
            return (
              <Card 
                key={u.id} 
                hoverable 
                style={{ borderRadius: 14 }} 
                bodyStyle={{ padding: 20 }}
              >
                {/* Header with user info and role */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      width: 44, 
                      height: 44, 
                      borderRadius: 10, 
                      background: '#3730a322', 
                      color: '#3730a3', 
                      display: 'grid', 
                      placeItems: 'center', 
                      fontSize: 20 
                    }}>
                      <UserOutlined />
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>{u.name || '—'}</div>
                      <div style={{ color: '#64748b', fontSize: 13 }}>{u.email}</div>
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: 12, 
                    background: '#eef2ff', 
                    color: '#3730a3', 
                    padding: '4px 12px', 
                    borderRadius: 999,
                    fontWeight: 500
                  }}>
                    {u.role || '—'}
                  </div>
                </div>
                <div style={{ 
                  marginTop: 12, 
                  padding: '12px', 
                  background: '#f8fafc', 
                  borderRadius: '8px', 
                  border: '1px solid #e2e8f0' 
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '8px' 
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                      Business Details
                    </div>
                    {isSuperAdmin && (
                      <Button 
                        size="small" 
                        type="link" 
                        style={{ padding: '2px 6px', fontSize: '11px', height: 'auto' }}
                        onClick={() => openBusinessEdit(u)}
                      >
                        {biz ? 'Edit' : 'Add'}
                      </Button>
                    )}
                  </div>
                  
                  {biz ? (
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      <div style={{ marginBottom: '4px' }}>
                        <strong style={{ color: '#374151' }}>Company:</strong> 
                        <span style={{ marginLeft: '6px', color: '#1f2937' }}>{biz.company_name || '—'}</span>
                      </div>
                      
                      {biz.industry && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#374151' }}>Industry:</strong> 
                          <span style={{ marginLeft: '6px', color: '#1f2937' }}>{biz.industry}</span>
                        </div>
                      )}
                      
                      {!isUserSuper && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#374151' }}>Plan:</strong> 
                          <span style={{ 
                            marginLeft: '6px', 
                            color: '#059669', 
                            fontWeight: '500',
                            textTransform: 'capitalize'
                          }}>{(up?.plan?.name || biz.subscription_plan || '—')}</span>
                        </div>
                      )}
                      
                      {biz.business_email && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#374151' }}>Email:</strong> 
                          <span style={{ marginLeft: '6px', color: '#1f2937' }}>{biz.business_email}</span>
                        </div>
                      )}
                      
                      {biz.contact && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#374151' }}>Contact:</strong> 
                          <span style={{ marginLeft: '6px', color: '#1f2937' }}>{biz.contact}</span>
                        </div>
                      )}
                      
                      {biz.website && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#374151' }}>Website:</strong> 
                          <a 
                            href={biz.website} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ 
                              marginLeft: '6px', 
                              color: '#2563eb', 
                              textDecoration: 'none',
                              fontSize: '11px'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {biz.website}
                          </a>
                        </div>
                      )}
                      
                      {biz.address && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#374151' }}>Address:</strong> 
                          <div style={{ 
                            marginTop: '2px', 
                            color: '#1f2937', 
                            fontSize: '11px',
                            lineHeight: '1.3',
                            paddingLeft: '4px',
                            borderLeft: '2px solid #e5e7eb',
                            marginLeft: '4px'
                          }}>
                            {biz.address}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280', 
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: '8px 0'
                    }}>
                      No business details available
                    </div>
                  )}
                </div>
                {/* Subscription Plan Section */}
                {!isUserSuper && up ? (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 12, 
                    padding: '12px 16px', 
                    background: '#f0f9ff', 
                    borderRadius: 8, 
                    marginTop: 16 
                  }}>
                    <div style={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: 6, 
                      background: '#0ea5e922', 
                      color: '#0ea5e9', 
                      display: 'grid', 
                      placeItems: 'center', 
                      fontSize: 16 
                    }}>
                      <CreditCardOutlined />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                        {up.plan?.name || '—'} Plan
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {up.plan?.price != null ? `£${up.plan.price}` : 'Free'} • 
                        {up.start_date ? ` Started ${new Date(up.start_date).toLocaleDateString()}` : ' No start date'}
                        {up.end_date ? ` • Ends ${new Date(up.end_date).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                  </div>
                ) : (!isUserSuper && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#f9fafb', 
                    borderRadius: 8, 
                    marginTop: 16,
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>
                      No active subscription
                    </div>
                  </div>
                ))}
                <Space style={{ marginTop: 12 }}>
                  <Button size="small" onClick={() => openEdit(u)}>Edit</Button>
                  <Popconfirm title="Delete user?" onConfirm={() => handleDelete(u.id)}>
                    <Button danger size="small">Delete</Button>
                  </Popconfirm>
                </Space>
              </Card>
            );
          })}
        </div>
      </div>

      <Modal
        title={editing ? 'Edit User' : 'Add User'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={editing ? 'Save' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter an email' }, { type: 'email', message: 'Invalid email' }]}>
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select allowClear options={roleOptions} placeholder="Select a role (optional)" />
          </Form.Item>
          {isSuperAdmin ? (
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const roleVal = getFieldValue('role') || editing?.role || '';
                const targetIsSuperForm = String(roleVal).toLowerCase().includes('super');
                return !targetIsSuperForm ? (
                  <Form.Item name="planId" label="Subscription Plan">
                    <Select
                      allowClear
                      placeholder="Select a subscription plan (optional)"
                      options={subscriptions.map(s => ({ label: `${s.name}${s.price != null ? ` (£${s.price})` : ''}`, value: s.id }))}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
          ) : null}
          {editing ? (
            <Form.Item name="password" label="Reset Password" tooltip="Leave empty to keep current password">
              <Input.Password placeholder="New password (optional)" />
            </Form.Item>
          ) : (
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please set a password' }, { min: 8, message: 'At least 8 characters' }]}>
              <Input.Password placeholder="At least 8 characters" />
            </Form.Item>
          )}
        </Form>
      </Modal>


      {/* Business Details Modal */}
      <Modal
        title={`Business Details - ${editingBusinessUser?.name || 'User'}`}
        open={businessModalOpen}
        onOk={handleBusinessSubmit}
        onCancel={() => {
          setBusinessModalOpen(false);
          businessForm.resetFields();
        }}
        okText="Save Business Details"
        confirmLoading={loading}
        width={500}
      >
        <Form form={businessForm} layout="vertical">
          <Form.Item 
            name="company_name" 
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>
          
          <Form.Item 
            name="industry" 
            label="Industry"
          >
            <Select
              placeholder="Select industry"
              showSearch
              allowClear
              options={[
                { label: 'Technology', value: 'technology' },
                { label: 'Healthcare', value: 'healthcare' },
                { label: 'Finance', value: 'finance' },
                { label: 'E-commerce', value: 'ecommerce' },
                { label: 'Education', value: 'education' },
                { label: 'Manufacturing', value: 'manufacturing' },
                { label: 'Real Estate', value: 'real_estate' },
                { label: 'Consulting', value: 'consulting' },
                { label: 'Marketing', value: 'marketing' },
                { label: 'Other', value: 'other' }
              ]}
            />
          </Form.Item>
          
          {!String(editingBusinessUser?.role || '').toLowerCase().includes('super') && (
            <Form.Item 
              name="subscription_plan" 
              label="Business Subscription Plan"
              tooltip="Select from available subscription plans"
            >
              <Select
                placeholder="Select business plan"
                allowClear
                options={subscriptions.map(s => ({ 
                  label: `${s.name}${s.price != null ? ` (£${s.price})` : ''}`, 
                  value: s.name.toLowerCase()
                }))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          )}

          <Form.Item 
            name="website" 
            label="Website URL"
            rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item 
            name="business_email" 
            label="Business Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="contact@company.com" />
          </Form.Item>

          <Form.Item 
            name="contact" 
            label="Contact Number"
          >
            <Input placeholder="+1 (555) 123-4567" />
          </Form.Item>

          <Form.Item 
            name="address" 
            label="Business Address"
          >
            <Input.TextArea 
              rows={3}
              placeholder="Enter business address"
            />
          </Form.Item>

          {/* Plan Date Fields - Only show if subscription plan is selected */}
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.subscription_plan !== currentValues.subscription_plan}>
            {({ getFieldValue }) => {
              const selectedPlan = getFieldValue('subscription_plan');
              return selectedPlan ? (
                <>
                  <Form.Item 
                    name="plan_start_date" 
                    label="Plan Start Date"
                    tooltip="When the subscription plan should start. Cannot be before today."
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (value.isBefore(dayjs().startOf('day'))) {
                            return Promise.reject(new Error('Start date cannot be before today'));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }}
                      placeholder="Select start date (defaults to today)"
                      disabledDate={(current) => current && current < dayjs().startOf('day')}
                    />
                  </Form.Item>
                  
                  <Form.Item 
                    name="plan_end_date" 
                    label="Plan End Date"
                    tooltip="When the subscription plan should end. Leave empty for unlimited duration."
                    dependencies={['plan_start_date']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const startDate = getFieldValue('plan_start_date');
                          if (startDate && value.isBefore(startDate)) {
                            return Promise.reject(new Error('End date cannot be before start date'));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }}
                      placeholder="Select end date (optional)"
                    />
                  </Form.Item>
                </>
              ) : null;
            }}
          </Form.Item>
        </Form>
        
        {/* Warning about plan changes */}
        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.subscription_plan !== currentValues.subscription_plan}>
          {({ getFieldValue }) => {
            const selectedPlan = getFieldValue('subscription_plan');
            const currentPlan = businessByUser[editingBusinessUser?.id]?.subscription_plan;
            return selectedPlan && selectedPlan !== currentPlan ? (
              <div style={{ 
                background: '#fff7ed', 
                border: '1px solid #fed7aa',
                borderRadius: '6px', 
                padding: '12px', 
                marginTop: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#ea580c', marginBottom: '4px' }}>
                  ⚠️ Plan Change Notice
                </div>
                <div style={{ fontSize: '12px', color: '#9a3412', lineHeight: '1.4' }}>
                  Changing the subscription plan will immediately end the current plan and start the new one. 
                  The user's current subscription will be terminated today.
                </div>
              </div>
            ) : null;
          }}
        </Form.Item>
        
        {editingBusinessUser && businessByUser[editingBusinessUser.id] && (
          <div style={{ 
            background: '#f6f8fa', 
            padding: '12px', 
            borderRadius: '6px', 
            marginTop: '16px',
            border: '1px solid #e1e4e8'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
              Current Business Details:
            </div>
            <div style={{ fontSize: '12px', color: '#586069' }}>
              <div><strong>Company:</strong> {businessByUser[editingBusinessUser.id]?.company_name || 'Not set'}</div>
              <div><strong>Industry:</strong> {businessByUser[editingBusinessUser.id]?.industry || 'Not set'}</div>
              <div><strong>Plan:</strong> {businessByUser[editingBusinessUser.id]?.subscription_plan || 'Not set'}</div>
              <div><strong>Website:</strong> {businessByUser[editingBusinessUser.id]?.website || 'Not set'}</div>
              <div><strong>Business Email:</strong> {businessByUser[editingBusinessUser.id]?.business_email || 'Not set'}</div>
              <div><strong>Contact:</strong> {businessByUser[editingBusinessUser.id]?.contact || 'Not set'}</div>
              <div><strong>Address:</strong> {businessByUser[editingBusinessUser.id]?.address || 'Not set'}</div>
              <div><strong>Created:</strong> {businessByUser[editingBusinessUser.id]?.created_at ? 
                new Date(businessByUser[editingBusinessUser.id].created_at).toLocaleDateString() : 'N/A'}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AllUsers;

